import React from 'react'
import { Table as AntTable } from 'antd'
import type { TableProps as AntTableProps } from 'antd/lib/table'

import './styles.css'

const DEFAULT_LOCALE = {
  triggerDesc: 'Click to sort in descending order',
  triggerAsc: 'Click to sort in ascending order',
}

export type TableVariant = 'indexPage' | 'segment' | 'fullViewport'

export type TableProps<RecordType = any> = AntTableProps<RecordType> & {
  variant?: TableVariant
  /**
   * When `true` (default), rows render with `cursor: pointer` to signal
   * they're clickable. Pass `false` for read-only tables (no `onRow` /
   * `onRowClick`) to avoid a misleading affordance.
   */
  clickable?: boolean
  /**
   * When `true`, the wrapper auto-applies the `ss-table-data` /
   * `ss-table-empty` class based on `dataSource.length`, so the existing
   * shared-Table viewport-height rules (`calc(100vh - 170/218/237px)`)
   * govern the body + empty-placeholder heights without the caller
   * needing to compute it. Combine with `className="… no-filter-bar"`
   * when there's no filter bar above the table (170px offset). Use for
   * table-only-page layouts like PivotTable on `/reports/`. Default `false`
   * for backward compatibility — existing callers that opt in via their
   * own className (e.g. `TableAntdLoadMore`) keep working unchanged.
   */
  fullViewportHeight?: boolean
}

/**
 * Shared antd Table wrapper.
 *
 * Defaults: `size="middle"`, `pagination={false}`, sort tooltips localized,
 * `variant="fullViewport"`. **No `scroll` default** — pass it explicitly.
 *

 * Styles live in `./styles.css`, scoped under `.ss-table` so they don't leak
 * to raw `antd` Tables elsewhere in dashboard (PivotTable, EmailActivityWidget,
 * etc.). The wrapper always sets `className="ss-table variant-indexPage|variant-segment|variant-fullViewport [is-clickable]"`.
 *
 * `clickable` prop (default `true`) toggles the `is-clickable` class which
 * applies `cursor: pointer` to rows. Pass `clickable={false}` for read-only
 * tables (no `onRow` / `onRowClick`) to avoid a misleading affordance.
 *
 * Variants — named by the host context they were designed for:
 *
 * - `variant="indexPage"` — rounded chrome (box-shadow + border + 10px radius
 *   + thead underline) AND index-page typography (**16px grey text** `#5a5a5a`,
 *   bold first column, grey `<a>` overrides on first cell). Use inside
 *   `<ContainerShell>` settings index pages: Workspaces, Roles, Topics, API
 *   Keys, settings/Surveys, Users — and a handful of modal-hosted settings
 *   tables (SelectWorkspace, ArchivedCustomDashboardModel) that share the
 *   same row-of-records archetype.
 * - `variant="segment"` — same rounded card chrome as `indexPage`, but
 *   WITHOUT the index-page typography overrides. 14px text inheriting
 *   `--ss-text-color`, no bold first column. Use for in-page panels and
 *   modal-hosted mapping tables that previously wrapped a default Table in a
 *   Semantic UI `<Segment>` for chrome — which double-bordered against this
 *   canonical chrome. Callers today: ImportConfirmation (field mapping +
 *   skipped-rows), FeedbackFieldMapper, CyclrCustomFieldForm.
 * - `variant="fullViewport"` (default) — **14px text** inheriting body's
 *   `--ss-text-color`, thead max-width 250px, no chrome. Matches legacy
 *   `StyledAntdTable`. Use for filter-bar data grids (Customers, TeamMembers,
 *   Responses, Email Activity, Insight AnswerTable).
 *
 * Column overflow policy (see `dashboard/.claude/rules/component-rules.md`):
 *
 * - Primary identifier (Name, Email, Key name) →
 *     `{ ...getAntdColumnMaxWidth(N), render: (v) => <Popup.Truncated content={v}/> }`
 * - Secondary text (Company, Role, Source) →
 *     `{ width: N, ellipsis: true }`
 * - Chip / badge / icon → `{ width: N }` (no ellipsis — already short)
 * - Action column → `{ width: N, align: 'right' }`
 *
 * Scroll policy:
 *
 * - Small fixed `variant="indexPage"` table → omit `scroll` (forcing it
 *   clips action-menu popups via the antd scroll container's overflow rules).
 * - Wide fixed-column table → `scroll={{ x: NUMBER }}`.
 * - Variable-width fullViewport data grid → `scroll={{ x: 'max-content' }}`.
 * - Tall sticky-header table → `scroll={{ x, y }}`.
 */
export default function Table<RecordType extends object = any>(props: TableProps<RecordType>) {
  const {
    variant = 'fullViewport',
    clickable = true,
    fullViewportHeight = false,
    className,
    locale,
    ...rest
  } = props
  const variantClassName =
    variant === 'indexPage'
      ? 'variant-indexPage'
      : variant === 'segment'
      ? 'variant-segment'
      : 'variant-fullViewport'
  const clickableClassName = clickable ? 'is-clickable' : ''
  // Data-presence drives the class — `ss-table-data` triggers the body
  // viewport-height rule, `ss-table-empty` triggers the placeholder rule.
  // Loading is handled separately by hiding the empty illustration via
  // `.ant-spin-blur .ant-empty { display: none }` so the spinner overlays a
  // clean placeholder area during initial loads.
  const dataStateClassName = fullViewportHeight
    ? (props.dataSource?.length ?? 0) > 0
      ? 'ss-table-data'
      : 'ss-table-empty'
    : ''
  const mergedClassName = [
    'ss-table',
    variantClassName,
    clickableClassName,
    dataStateClassName,
    className,
  ]
    .filter(Boolean)
    .join(' ')
  const mergedLocale = { ...DEFAULT_LOCALE, ...(locale || {}) }
  return (
    <AntTable
      size="middle"
      pagination={false}
      className={mergedClassName}
      locale={mergedLocale}
      {...(rest as AntTableProps<unknown>)}
    />
  )
}



// WEBPACK FOOTER //
// ./src/shared/components/Table/index.tsx